import React from "react";

/**
 * ExerciseOverlay - Modal overlay for displaying exercise details
 * All fields are optional except id
 */
export default function ExerciseOverlay({
    exercise,
    onClose,
    canToggleComplete = false,
    isCompleted = false,
    isSavingCompletion = false,
    completionError = "",
    onToggleComplete,
}) {
    // Handle click outside to close
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Handle escape key
    React.useEffect(() => {
        if (!exercise) return;
        const handleEscape = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [exercise, onClose]);

    // Prevent body scroll when overlay is open
    React.useEffect(() => {
        if (!exercise) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [exercise]);

    // Early return after hooks
    if (!exercise) return null;

    const hasSections = Array.isArray(exercise.sections) && exercise.sections.length > 0;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center overflow-x-hidden p-2 sm:items-center sm:p-3 md:p-4"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
            onClick={handleBackdropClick}
        >
            <div
                className={`relative w-full min-w-0 max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-1.5rem)] ${hasSections ? "md:max-w-4xl" : "md:max-w-2xl"} max-h-[94vh] overflow-y-auto overflow-x-hidden overscroll-contain rounded-[28px] p-3 sm:p-4 md:p-8`}
                style={{
                    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 md:top-4 md:right-4 w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                    style={{
                        background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                        border: "1px solid rgba(0, 0, 0, 0.1)"
                    }}
                >
                    <span className="text-xl" style={{ color: "#64748b" }}>✕</span>
                </button>

                {/* Title */}
                {exercise.title && (
                    <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 pr-10 md:pr-12 break-words" style={{ color: "#1e293b" }}>
                        {exercise.title}
                    </h2>
                )}

                {/* Description - renders line breaks from \n characters */}
                {exercise.description && (
                    <div className="text-sm sm:text-base md:text-lg mb-5 md:mb-6 break-words" style={{ color: "#64748b", lineHeight: 1.7 }}>
                        {exercise.description.split('\n').map((paragraph, index, arr) => (
                            <React.Fragment key={index}>
                                {paragraph}
                                {index < arr.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {canToggleComplete && typeof onToggleComplete === "function" && exercise.progressKey && (
                    <div className="mb-5 md:mb-6">
                        <button
                            type="button"
                            onClick={onToggleComplete}
                            disabled={isSavingCompletion}
                            className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70"
                            style={{
                                background: isCompleted
                                    ? "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)"
                                    : "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                                border: isCompleted
                                    ? "1px solid rgba(22, 163, 74, 0.35)"
                                    : "1px solid rgba(34, 197, 94, 0.24)",
                                color: isCompleted ? "#ffffff" : "#166534",
                                boxShadow: isCompleted
                                    ? "0 10px 30px rgba(34, 197, 94, 0.2)"
                                    : "none",
                            }}
                        >
                            <span className="block text-xs sm:text-sm font-bold">
                                {isSavingCompletion
                                    ? "Saving progress..."
                                    : isCompleted
                                        ? "Completed"
                                        : "Mark as completed"}
                            </span>
                        </button>

                        {isCompleted && !isSavingCompletion && (
                            <p className="text-xs mt-2 mb-0" style={{ color: "#64748b" }}>
                                Tap again if you want to mark this day as incomplete.
                            </p>
                        )}

                        {completionError && (
                            <p className="text-sm mt-2 mb-0" style={{ color: "#b91c1c" }}>
                                {completionError}
                            </p>
                        )}
                    </div>
                )}

                {hasSections && (
                    <div className="grid gap-4 md:gap-5 mb-6 min-w-0">
                        {exercise.sections.map((contentSection) => {
                            const items = contentSection.items || [];
                            const sectionKey = contentSection.id || contentSection.title || "section";

                            if (contentSection.hideTitle) {
                                return (
                                    <div key={sectionKey} className="grid gap-4">
                                        {items.map((item) => (
                                            <ExerciseDetailCard
                                                key={`${sectionKey}-${item.title}`}
                                                item={item}
                                            />
                                        ))}
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={sectionKey}
                                    className="rounded-3xl p-3 sm:p-4 md:p-5"
                                    style={{
                                        background: "linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%)",
                                        border: "1px solid rgba(99, 102, 241, 0.14)",
                                    }}
                                >
                                    {contentSection.title && (
                                        <h3 className="text-lg md:text-xl font-bold mb-4" style={{ color: "#1e293b" }}>
                                            {contentSection.title}
                                        </h3>
                                    )}

                                    <div className="grid gap-4">
                                        {items.map((item) => (
                                            <ExerciseDetailCard
                                                key={`${sectionKey}-${item.title}`}
                                                item={item}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Vimeo Embed */}
                {exercise.vimeoId && (
                    <div className="mb-6 rounded-2xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
                        <iframe
                            src={`https://player.vimeo.com/video/${exercise.vimeoId}?h=0&title=0&byline=0&portrait=0`}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={exercise.title || "Exercise Video"}
                        />
                    </div>
                )}

                {/* Second Vimeo Embed (optional) */}
                {exercise.vimeoId2 && (
                    <div className="mb-6 rounded-2xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
                        <iframe
                            src={`https://player.vimeo.com/video/${exercise.vimeoId2}?h=0&title=0&byline=0&portrait=0`}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={`${exercise.title || "Exercise"} - Video 2`}
                        />
                    </div>
                )}

                {/* Link Button */}
                {exercise.link && exercise.link.url && (
                    <a
                        href={exercise.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl px-6 py-3 font-semibold text-base transition-all duration-300 hover:scale-[1.03] hover:shadow-lg no-underline"
                        style={{
                            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                            color: "white",
                            boxShadow: "0 4px 15px rgba(99, 102, 241, 0.3)"
                        }}
                    >
                        {exercise.link.text || "View Resource"}
                        <span>→</span>
                    </a>
                )}
            </div>
        </div>
    );
}

function ExerciseDetailCard({ item }) {
    const setsReps = item.meta?.find((entry) => entry.label === "Sets/Reps")?.value;
    const realLifeBenefit = item.meta?.find((entry) => entry.label === "Real-life benefit")?.value;
    const [imageFailed, setImageFailed] = React.useState(false);
    const hasImage = Boolean(item.image) && !imageFailed;

    React.useEffect(() => {
        setImageFailed(false);
    }, [item.image]);

    return (
        <div
            className="rounded-3xl p-3 sm:p-4 md:p-5 min-w-0 overflow-hidden"
            style={{
                background: "rgba(255, 255, 255, 0.98)",
                border: "1px solid rgba(148, 163, 184, 0.18)",
            }}
        >
            <div className="grid min-w-0 grid-cols-[92px_minmax(0,1fr)] gap-3 sm:grid-cols-[120px_minmax(0,1fr)] sm:gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:gap-5">
                {hasImage ? (
                    <div
                        className="rounded-2xl min-h-[92px] sm:min-h-[120px] md:min-h-[160px] overflow-hidden"
                        style={{
                            background: "linear-gradient(135deg, #eef4ff 0%, #f8fafc 100%)",
                            border: "1px solid rgba(148, 163, 184, 0.18)",
                        }}
                    >
                        <img
                            src={item.image}
                            alt={item.title || "Exercise"}
                            className="w-full h-full object-cover"
                            onError={() => setImageFailed(true)}
                        />
                    </div>
                ) : (
                    <div
                        className="rounded-2xl min-h-[92px] sm:min-h-[120px] md:min-h-[160px] flex items-center justify-center text-[11px] sm:text-sm font-semibold text-center px-2"
                        style={{
                            background: "linear-gradient(135deg, #eef4ff 0%, #f8fafc 100%)",
                            border: "1px dashed rgba(99, 102, 241, 0.24)",
                            color: "#64748b",
                        }}
                    >
                        Image Placeholder
                    </div>
                )}

                <div className="min-w-0">
                    {item.title && (
                        <h4 className="text-sm sm:text-lg md:text-xl font-bold mb-1.5 break-words" style={{ color: "#1e293b" }}>
                            {item.title}
                        </h4>
                    )}

                    {setsReps && (
                        <p className="text-xs sm:text-sm md:text-base font-semibold mb-2 break-words" style={{ color: "#4f46e5" }}>
                            Sets/Reps: {setsReps}
                        </p>
                    )}

                    {item.description && (
                        <p className="text-xs sm:text-sm md:text-base m-0 break-words" style={{ color: "#64748b", lineHeight: 1.6 }}>
                            {item.description}
                        </p>
                    )}
                </div>
            </div>

            {realLifeBenefit && (
                <div className="mt-4">
                    <p
                        className="text-[11px] sm:text-xs md:text-sm m-0 truncate"
                        style={{ color: "#0f766e", lineHeight: 1.4 }}
                        title={`Real-life benefit: ${realLifeBenefit}`}
                    >
                        <span className="font-semibold">Real-life benefit:</span> {realLifeBenefit}
                    </p>
                </div>
            )}
        </div>
    );
}
